
/**
 * ORION — Cognition Domain
 *
 * File: cognitionService.ts
 *
 * Responsibility:
 * - Frontend Service layer for Cognitive Objects.
 * - CRUD operations (Create, Read, Update).
 * - Lifecycle management (Fork, Merge, Transition).
 *
 * What this file does NOT do:
 * - Does not handle UI interaction.
 * - Does not implement voting logic.
 */
import axios from 'axios';
import { getToken } from '../../utils/auth';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

const client = axios.create({
    baseURL: API_URL
});

// Interceptor to add token
client.interceptors.request.use(config => {
    const token = getToken();
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

export interface CognitiveObject {
    id: string;
    object_type: string;
    created_at: string;
    updated_at: string;
    version: number;
    current_state: string;
    content: any;
    metadata: any;
}

export const cognitionService = {
    async listObjects(state?: string, objType?: string, limit: number = 20) {
        const params: any = { limit };
        if (state) params.state = state;
        if (objType) params.type = objType;

        return (await client.get<CognitiveObject[]>('/cognition/objects', { params })).data;
    },

    async getObject(id: string) {
        return (await client.get<CognitiveObject>(`/cognition/objects/${id}`)).data;
    },

    async createObject(obj: Partial<CognitiveObject>) {
        return (await client.post<CognitiveObject>('/cognition/objects', obj)).data;
    },

    async updateObject(id: string, updates: Partial<CognitiveObject>) {
        return (await client.patch<CognitiveObject>(`/cognition/objects/${id}`, updates)).data;
    },

    async forkObject(id: string) {
        return (await client.post<CognitiveObject>(`/cognition/objects/${id}/fork`)).data;
    },

    async mergeObject(forkId: string, parentId: string, strategy: string) {
        return (await client.post<CognitiveObject>(`/cognition/objects/${parentId}/merge`, {
            fork_id: forkId,
            strategy
        })).data;
    },

    async transitionState(id: string, event: string, payload?: any) {
        // Human transition
        return (await client.post<CognitiveObject>(`/cognition/objects/${id}/transition`, {
            event,
            payload
        })).data;
    },

    async getVersions(id: string) {
        return (await client.get<any[]>(`/cognition/objects/${id}/versions`)).data;
    }
};
